/**
 * NullEngine adapter for testing without WebGL
 */

import { NullEngine, type Scene } from "@babylonjs/core";
import type { AnyEngine, EngineOptions } from "../types";
import type { IEngineAdapter } from "./IEngineAdapter";

/**
 * Adapter for NullEngine - used for testing without WebGL/GPU
 * Provides 10-100x faster test execution
 */
export class NullEngineAdapter implements IEngineAdapter {
  private _engine: NullEngine | null = null;
  private _isInitialized = false;
  private _renderLoopRunning = false;

  /**
   * The underlying NullEngine instance
   */
  get engine(): AnyEngine | null {
    return this._engine;
  }

  /**
   * The type of engine adapter
   */
  get type(): string {
    return "null";
  }

  /**
   * Whether the engine is initialized
   */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Create and initialize the NullEngine
   * @param options Engine creation options
   */
  async createEngine(options: EngineOptions): Promise<AnyEngine> {
    if (this._engine) {
      throw new Error("Engine already created");
    }

    const nullOptions = options.nullEngineOptions || {};

    this._engine = new NullEngine({
      deterministicLockstep: nullOptions.deterministicLockstep || false,
      lockstepMaxSteps: nullOptions.lockstepMaxSteps || 4,
      renderHeight: nullOptions.renderHeight || 256,
      renderWidth: nullOptions.renderWidth || 512,
      textureSize: nullOptions.textureSize || 512,
    });

    this._isInitialized = true;

    return this._engine;
  }

  /**
   * Start the render loop
   * @param renderFunction Function to call on each frame
   */
  startRenderLoop(renderFunction: () => void): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    this._renderLoopRunning = true;
    this._engine.runRenderLoop(renderFunction);
  }

  /**
   * Stop the render loop
   */
  stopRenderLoop(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    this._renderLoopRunning = false;
    this._engine.stopRenderLoop();
  }

  /**
   * Resize the engine (no-op for NullEngine)
   * @param _width New width (ignored)
   * @param _height New height (ignored)
   */
  resize(_width?: number, _height?: number): void {
    // NullEngine doesn't need resizing
    // This is a no-op but satisfies the interface
  }

  /**
   * Begin a frame
   */
  beginFrame(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }
    this._engine.beginFrame();
  }

  /**
   * End a frame
   */
  endFrame(): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }
    this._engine.endFrame();
  }

  /**
   * Render a scene
   * @param scene The scene to render
   */
  renderScene(scene: Scene): void {
    if (!this._engine) {
      throw new Error("Engine not created");
    }

    // NullEngine rendering (no actual GPU operations)
    scene.render();
  }

  /**
   * Dispose of the engine and clean up resources
   */
  dispose(): void {
    if (this._renderLoopRunning) {
      this.stopRenderLoop();
    }

    if (this._engine) {
      this._engine.dispose();
      this._engine = null;
    }

    this._isInitialized = false;
  }

  /**
   * Get the rendering canvas (null for NullEngine)
   */
  getCanvas(): HTMLCanvasElement | null {
    // NullEngine doesn't use a real canvas
    return null;
  }

  /**
   * Get the current frames per second
   */
  getFps(): number {
    if (!this._engine) {
      return 0;
    }
    return this._engine.getFps();
  }

  /**
   * Get delta time between frames
   */
  getDeltaTime(): number {
    if (!this._engine) {
      return 0;
    }
    return this._engine.getDeltaTime();
  }

  /**
   * Check if the engine supports a feature
   * @param feature Feature name to check
   */
  supportsFeature(feature: string): boolean {
    if (!this._engine) {
      return false;
    }

    // NullEngine supports most features for testing
    // but not actual rendering features
    switch (feature) {
      case "webgl":
      case "webgpu":
      case "webxr":
        return false;
      case "testing":
      case "nullengine":
        return true;
      default:
        return true; // Assume support for testing purposes
    }
  }
}
